/**
 * THE DOCUMENT-TITLE FONT — Inter Black, embedded (Vinnie, 13 Oct 2026).
 *
 * The produced documents wear one heavy face for the single big subject on the
 * letterhead — the project's name on a charter, the reference on an authority to
 * pay — so the title reads with the weight a signed document deserves. It is
 * Inter, the app's own typeface, at its heaviest (Black, 900), so a printed
 * charter and the charter on screen are plainly the same product.
 *
 * WHY IT IS EMBEDDED, when the body is not. The body stays on the base-14 fonts
 * (Helvetica), which need no embedding, keep the text searchable, and render the
 * same on every reader. A title in Inter has to carry its own glyphs, or a reader
 * without Inter installed would see it fall back to something else — so the font
 * travels inside the file. fontkit is what lets pdf-lib embed a non-base-14 font;
 * it is registered on the document once, before the font is embedded, and pdf-lib
 * then subsets the embedded copy to only the glyphs the title actually uses, so
 * the weight it adds to each document is a couple of kilobytes, not the whole face.
 *
 * Inter ships as a variable font; the bundled file is the Black instance, pinned
 * and pre-trimmed to the Latin range a title uses (see src/assets/fonts). Loaded
 * once at runtime and cached, so a workspace that produces ten documents fetches
 * the face once.
 */
import fontkit from '@pdf-lib/fontkit';
import type { PDFDocument, PDFFont } from 'pdf-lib';
import interBlackUrl from './assets/fonts/Inter-Black.ttf?url';

/* The face's bytes, fetched once. A produce is rare and deliberate, so a single
   cached promise is all the sharing this needs. */
let bytes: Promise<ArrayBuffer> | null = null;
function titleFontBytes(): Promise<ArrayBuffer> {
  if (!bytes) bytes = fetch(interBlackUrl).then((r) => r.arrayBuffer());
  return bytes;
}

/**
 * Register fontkit on a document and embed the title face, subset to the glyphs
 * this document uses. Called once per produced PDF, before the letterhead is
 * drawn; the returned font is handed to the letterhead as its title face. The
 * same bytes embedded the same way make the same file, so this keeps a produced
 * document a pure function of its content (the charter and the authority to pay
 * fingerprint on that).
 */
export async function embedTitleFont(pdf: PDFDocument): Promise<PDFFont> {
  pdf.registerFontkit(fontkit);
  return pdf.embedFont(await titleFontBytes(), { subset: true });
}
